import { useEffect, useRef, useSyncExternalStore } from "react";
import { DESKTOP_COMMANDS, MAIN_COMMANDS, type DesktopCommand } from "@ide/shared";
import { desktop } from "./bridge";

/**
 * Comandos dos menus do app desktop (barra de título e menu nativo). Cada parte da interface
 * registra o que sabe fazer; a camada mais recente vence (o projeto aberto vem antes do app).
 */
type Handlers = Partial<Record<DesktopCommand, () => void>>;

const layers: Handlers[] = [];
const listeners = new Set<() => void>();
let version = 0;
let reportQueued = false;

const isMain = (c: DesktopCommand) => MAIN_COMMANDS.includes(c);

export function isAvailable(command: DesktopCommand): boolean {
  return isMain(command) ? desktop !== null : layers.some((l) => l[command]);
}

export const availableCommands = () => DESKTOP_COMMANDS.filter(isAvailable);

function changed() {
  version++;
  listeners.forEach((l) => l());
  // O menu nativo habilita/desabilita os itens conforme a página (um aviso por rodada).
  if (desktop && !reportQueued) {
    reportQueued = true;
    queueMicrotask(() => {
      reportQueued = false;
      desktop?.setAvailableCommands(availableCommands());
    });
  }
}

export function runCommand(command: DesktopCommand) {
  if (isMain(command)) {
    desktop?.runMainCommand(command);
    return;
  }
  for (let i = layers.length - 1; i >= 0; i--) {
    const handler = layers[i]![command];
    if (handler) return handler();
  }
}

/** Registra comandos enquanto o componente está montado (as funções podem mudar a cada render). */
export function useCommands(handlers: Handlers) {
  const latest = useRef(handlers);
  useEffect(() => {
    latest.current = handlers;
  });
  const ids = Object.keys(handlers).sort().join(",");
  useEffect(() => {
    const layer: Handlers = {};
    for (const id of ids.split(",").filter(Boolean) as DesktopCommand[]) {
      layer[id] = () => latest.current[id]?.();
    }
    layers.push(layer);
    changed();
    return () => {
      layers.splice(layers.indexOf(layer), 1);
      changed();
    };
  }, [ids]);
}

/** Re-renderiza quando a lista de comandos disponíveis muda (itens habilitados no menu). */
export function useCommandsVersion() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => version,
  );
}
